import { ref, computed } from 'vue'
import { usePageLoading } from '@/hooks/use-page-loading'
import type { ProjectInfo, ProjectListParams } from '@/api/project.type'
import { apiGetProjectList } from '@/api/project'
import type { ProjectEpisodeInfo } from '@/modules/ai-video/type'
import { apiGetProjectEpisodeList } from '@/modules/ai-video/ai-video-api'

const isProjectListInitialized = ref(false)
const self_studio_id = ref<number>(0)
// 项目列表状态
const projectList = ref<ProjectInfo[]>([])
// 当前项目状态
const currentProject = ref<ProjectInfo | null>(null)
// 剧集列表
const episodeList = ref<ProjectEpisodeInfo[]>([])
// 当前剧集
const currentEpisode = ref<ProjectEpisodeInfo | null>(null)

// 页面加载状态
const pageLoading = ref(false)
// 剧集列表加载状态
const episodeLoading = ref(false)
// 创建/编辑剧集弹窗显示状态
const addEpisodeVisible = ref(false)

// 创建/编辑剧集表单数据
type CreateEpisodeForm = {
  project_id: number
  title: string
  pic_count: number
  status: number
  episode_id?: number
}

const initCreateEpisodeForm: CreateEpisodeForm = {
  project_id: 0,
  title: '',
  pic_count: 1,
  status: 1,
}

const createEpisodeForm = ref<CreateEpisodeForm>({
  project_id: 0,
  title: '',
  pic_count: 1,
  status: 1,
  episode_id: undefined,
})

// 计算属性：当前项目ID
const currentProjectId = computed(() => currentProject.value?.id || 0)

// 计算属性：当前剧集ID（episode_num）
const currentEpisodeId = computed(() => currentEpisode.value?.episode_id || 0)

// 全局 loading 辅助函数
const withLoading = async <T>(fn: () => Promise<T>, text = ''): Promise<T> => {
  const { showPageLoading, hidePageLoading } = usePageLoading()
  showPageLoading()
  try {
    return await fn()
  } finally {
    hidePageLoading()
  }
}

// 获取项目列表
const fetchProjectList = async (params?: ProjectListParams, showLoading = true) => {
  pageLoading.value = true
  try {
    const res = await apiGetProjectList(params || {})
    isProjectListInitialized.value = true
    self_studio_id.value = res.data?.self_studio_id || 0
    if (res.code === 200 && res.data?.list) {
      projectList.value = res.data.list
      return res.data.list
    }
    return []
  } catch (error) {
    console.error('获取项目列表失败:', error)
    return []
  } finally {
    pageLoading.value = false
  }
}

// 设置当前项目
const setCurrentProject = (project: ProjectInfo) => {
  currentProject.value = project
}

// 根据项目ID设置当前项目
const setCurrentProjectById = (projectId: number) => {
  const project = projectList.value.find(p => p.id === projectId)
  if (project) {
    setCurrentProject(project)
  }
}

// 获取剧集列表
const fetchEpisodeList = async (projectId: number) => {
  episodeLoading.value = true
  try {
    const res = await apiGetProjectEpisodeList({ project_id: projectId })
    if (res.code === 200 && res.data?.list) {
      episodeList.value = res.data.list
      return res.data.list
    }
    return []
  } catch (error) {
    console.error('获取剧集列表失败:', error)
    return []
  } finally {
    episodeLoading.value = false
  }
}

// 设置当前剧集
const setCurrentEpisode = (episode: ProjectEpisodeInfo) => {
  currentEpisode.value = episode
}

// 根据剧集ID设置当前剧集
const setCurrentEpisodeById = (episodeId: number) => {
  const episode = episodeList.value.find(e => e.episode_id === episodeId)
  if (episode) {
    setCurrentEpisode(episode)
  }
}

// 重置创建剧集表单
const resetCreateEpisodeForm = () => {
  createEpisodeForm.value = JSON.parse(JSON.stringify({ ...initCreateEpisodeForm, status: 1 }))
}

// 设置创建剧集表单（用于编辑）
const setCreateEpisodeForm = (episode: Partial<CreateEpisodeForm>) => {
  createEpisodeForm.value = JSON.parse(JSON.stringify({ ...initCreateEpisodeForm, ...episode }))
}

// 重置项目状态
const resetProjectStore = () => {
  isProjectListInitialized.value = false
  self_studio_id.value = 0
  projectList.value = []
  currentProject.value = null
  episodeList.value = []
  currentEpisode.value = null
  pageLoading.value = false
  addEpisodeVisible.value = false
  createEpisodeForm.value = JSON.parse(JSON.stringify({ ...initCreateEpisodeForm, status: 1 }))
}

export const useProjectStore = () => {
  return {
    // 状态
    isProjectListInitialized,
    self_studio_id,
    projectList,
    currentProject,
    episodeList,
    currentEpisode,
    pageLoading,
    episodeLoading,
    addEpisodeVisible,
    createEpisodeForm,
    initCreateEpisodeForm,
    // 计算属性
    currentProjectId,
    currentEpisodeId,
    // 方法
    fetchProjectList,
    setCurrentProject,
    setCurrentProjectById,
    fetchEpisodeList,
    setCurrentEpisode,
    setCurrentEpisodeById,
    resetCreateEpisodeForm,
    setCreateEpisodeForm,
    resetProjectStore,
  }
}
